<i18n>
  {
    "en": {
      "log": {
        "network": {
          "agent": "Network"
        }
      }
    },
    "ru": {
      "log": {
        "network": {
          "title": "Сеть"
        }
      }
    }
  }
</i18n>

<template>
  <div>
    <div class="content-block dx-card responsive-paddings">
      <dx-form
        v-if="log"
        id="form"
        label-location="top"
        :form-data="log"
        :col-count="1"
        :read-only="true"
        validation-group="userValidation"
      >
        <dx-group-item :colCountByScreen="colCountByScreen">
          <dx-simple-item data-field="logDatetime"
            :editor-options="{ type:'datetime', displayFormat: 'dd.MM.yyyy HH:mm:ss' }"
          >
            <dx-label :text="$t('logs.date')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="eventGroup.name">
            <dx-label :text="$t('logs.group')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="logLevelText"
            :editor-options="logLevelEditorOptions"
          >
            <dx-label :text="$t('logs.recordtype')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item v-if="log.objectId" data-field="objectId">
            <dx-label :text="$t('logs.object')" :show-colon="false" />
            <template #default>
              <DxTextBox
                :readOnly="true"
                @content-ready="onContentReadyTextBox"
              />
            </template>
          </dx-simple-item>

          <dx-simple-item data-field="userDisplayName">
            <dx-label :text="$t('logs.user')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="messageCode">
            <dx-label :text="$t('logs.messagecode')" :show-colon="false" />
          </dx-simple-item>

          <dx-simple-item data-field="logDuration"
            :editor-options="{ type:'number', displayFormat: '#,##0.000' }"
          >
            <dx-label :text="$t('logs.time')" :show-colon="false" />
          </dx-simple-item>
        </dx-group-item>

        <dx-group-item :visible="log.message && log.message.length > 0">
          <dx-simple-item>
            <dx-label :text="$t('logs.message')" />
            <template #default>
              <div class="app-log-message">
                <template v-for="(msg, index) in log.message">
                  <p :key="index">
                    <router-link v-if="log.parentId && index === 0" class="level-up"
                      :to="logRoute(log.parentId)"
                      :title="$t('logs.toparentrecord')"
                    >
                      <font-awesome-icon class="dx-icon" icon="level-up-alt" />
                    </router-link>
                    {{ msg }}
                  </p>
                </template>
              </div>
            </template>
          </dx-simple-item>
        </dx-group-item>

        <dx-group-item :visible="log.stackTrace && log.stackTrace.length > 0">
          <dx-simple-item>
            <dx-label :text="$t('logs.stacktrace')" />
            <template #default>
              <div class="app-log-message">
                <template v-for="(msg, index) in log.stackTrace">
                  <pre :key="index">{{ msg }}</pre>
                </template>
              </div>
            </template>
          </dx-simple-item>
        </dx-group-item>

        <dx-group-item :visible="log.childrenCount > 0">
          <dx-simple-item>
            <dx-label :text="$t('logs.title')" />
            <template #default>
              <div class="app-log-table">
                <dx-data-grid
                  class="data-grid"
                  :data-source="childrenDataSourceConfig"
                  :height="gridHeight"
                  :show-borders="true"
                  @row-click="onRowClick"
                  @context-menu-preparing="addMenuItems"
                  @editor-preparing="onEditorPreparing"
                  @content-ready="onContentReady"
                  @initialized=initializedGrid
                  ref="dataGrid"
                >
                  <dx-paging :enabled="true"
                    :page-size="rowCountLogDetail" :page-index="pageIndex"
                  />
                  <dx-pager
                    :visible="true"
                    :allowed-page-sizes="[20, 50, 100, $t('general.pageHeight')]"
                    display-mode="full"
                    :show-page-size-selector="true"
                    :show-info="false"
                    :show-navigation-buttons="false"
                  />

                  <dx-sorting mode="multiple" />
                  <dx-state-storing
                    :enabled="true"
                    type="custom"
                    :customLoad="stateLoad"
                    :customSave="stateSave"
                  />

                  <dx-column
                    data-field="logDatetime"
                    :caption="$t('logs.date')"
                    :width="75"
                    data-type="date"
                    format="HH:mm:ss"
                    :allow-header-filtering="false"
                    :editor-options="{ calendarOptions: { firstDayOfWeek: 1 } }"
                  />
                  <dx-column
                    data-field="group.name"
                    :caption="$t('logs.group')"
                    :width="175"
                  />
                  <dx-column
                    data-field="logLevelText"
                    :caption="$t('logs.recordtype')"
                    :width="165"
                    cell-template="log-level-cell"
                  />
                  <dx-column
                    data-field="userDisplayName"
                    :caption="$t('logs.user')"
                    :width="140"
                  />
                  <dx-column
                    data-field="childrenCount"
                    :caption="$t('logs.children')"
                    :width="50"
                  />
                  <dx-column
                    data-field="logDuration"
                    :caption="$t('logs.time')"
                    :width="90"
                    data-type="number"
                    format="#,##0.000"
                    :customizeText="customizeText"
                  />
                  <dx-column
                    data-field="message"
                    :caption="$t('logs.message')"
                  />

                  <template #log-level-cell="{ data }">
                    <div :style="{ color: formatLogKindColor(data.data.logLevel) }">{{ data.displayValue }}</div>
                  </template>
                </dx-data-grid>

                <CustomColumnChooser
                  :visible="visible"
                  :columns="columns"
                  :onApply="onApply"
                  :onHiding="onHiding"
                />
              </div>
            </template>
          </dx-simple-item>
        </dx-group-item>
      </dx-form>
    </div>
  </div>
</template>

<script>
import appInfo from "../../app-info";
import baseView from "../base.vue";

import { DxForm, DxSimpleItem, DxLabel, DxGroupItem } from 'devextreme-vue/form';
import DxDataGrid, { DxColumn, DxStateStoring, DxPaging, DxPager, DxSorting } from "devextreme-vue/data-grid";
import DxTextBox from 'devextreme-vue/text-box';
import CustomStore from 'devextreme/data/custom_store';

import { getSystemLog } from "@/services/api";
import ServerEvents from "../../services/server-events";
import { fullUserName } from "@/utils/user";

import { SystemLogKind } from "./constants";
import CustomColumnChooser from "@/components/column-chooser/CustomColumnChooser.vue";
import _ from "underscore";

import { DataDrid } from "@/mixins/dataDridMixin";

import {
  PAGE_HEADER_HEIGHT,
  PAGE_PADDING,
  PAGE_TOOLBARS_HEIGHT,
  PAGE_FOOTER_HEIGHT,
  GRID_HEADER_HEIGHT,
  GRID_PAGER_HEIGHT
} from '@/shared/consts/layout';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faLevelUpAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';

library.add(faLevelUpAlt);

const formatLogKindText = key =>
  key in SystemLogKind ? SystemLogKind[key].text : key;
const formatLogKindColor = key => {
  return key in SystemLogKind ? SystemLogKind[key].color : "unset";
}

export default {
  props: ["id"],
  extends: baseView,
  mixins: [DataDrid],
  components: {
    DxForm, DxSimpleItem, DxLabel, DxGroupItem, DxDataGrid, DxColumn, FontAwesomeIcon,
    DxStateStoring, CustomColumnChooser, DxPaging, DxPager, DxSorting, DxTextBox
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      pageIndex: 0,
      log: null,
      colCountByScreen: {
        xs: 1,
        sm: 2,
        md: 3,
        lg: 6
      },
      childrenDataSourceConfig: {
        store: new CustomStore({
          key: 'id',
          load: () => this.log.children
        }),
        map: rec => {
          rec.logLevelText = formatLogKindText(rec.logLevel);
          rec.userDisplayName = fullUserName(rec.user);
          return rec;
        }
      },
      serverEvents: null,
      updateTimeout: null
    };
  },
  computed: {
    dataGrid() {
      return this.$refs.dataGrid? this.$refs.dataGrid.instance: null;
    },
    sumGap() {
      return PAGE_HEADER_HEIGHT + PAGE_PADDING + PAGE_TOOLBARS_HEIGHT + PAGE_FOOTER_HEIGHT;
    },
    gridHeight() {
      const appHeightSize = this.$store.getters.appContainerSize.height;

      return (appHeightSize || 800) - this.sumGap;
    },
    gridBodyHeight() {
      return this.gridHeight - GRID_HEADER_HEIGHT - GRID_PAGER_HEIGHT;
    },
    rowCountLogDetail() {
      return this.countRows(this.gridBodyHeight);
    },
    logLevelEditorOptions() {
      const color = formatLogKindColor(this.log.logLevel);
      return {
        inputAttr: { style: `color: ${color};` }
      };
    }
  },
  methods: {
    customizeText(cellData) {
      return cellData.value ? (cellData.value / 1000).toString() : '';
    },
    formatLogKindColor,
    logRoute(id) {
      const route = {
        name: "log",
        params: { id }
      };

      if (this.$route.query && this.$route.query.context === "agent-logs") {
        route.query = { context: "agent-logs" };
      }

      return route;
    },
    logBreadcrumb() {
      const breadcrumb = [{
        link: "/logs",
        title: `${this.$t('logs.title')}`
      }, {
        link: `/log/${this.id}`,
        title: this.id
      }];

      if (this.$route.query && this.$route.query.context === "agent-logs") {
        breadcrumb.forEach(item => delete item.link);
      }

      return breadcrumb;
    },
    onEditorPreparing(e) {
      this.applyFilterRowTimeout(e);
    },
    onContentReady(e) {
      this.addFitToPageHeightButton(e);
    },
    onContentReadyTextBox(e) {
      let href = null;
      let title = null;
      switch (this.log.objectClass) {
        case 'UserDao':
          href = `#/user/${this.log.objectId}`;
          title = this.$t('user.user');
          break;
        case 'AgentDao':
          href = `#/agent/${this.log.objectId}`;
          title = this.$t('queue.agent');
          break;
        case 'NetworkRangeDao':
          href = `#/network/${this.log.objectId}`;
          title = this.$t('log.network.title');
          break;
      
        default:
          break;
      }

      if (href) {
        const link = `<div class="dx-texteditor-input"><a href="${href}" target="_blank">${title}: ${this.log.objectId}</a></div>`;
        e.element.innerHTML = link;
      }
      
    },
    formatLog(log) {
      log.logDatetime = new Date(log.logDatetime);
      log.logLevelText = formatLogKindText(log.logLevel);
      if (log.user) {
        log.userDisplayName = fullUserName(log.user);
      }
      if (("logDuration" in log) && !_.isNull(log.logDuration)) {
        log.logDuration /= 1000;
      }
      if (log.message) {
        log.message = log.message.split("\n");
        log.message = _.isArray(log.message) ? log.message : [log.message];
      }
      if (log.stackTrace) {
        log.stackTrace = log.stackTrace.split("\n");
        log.stackTrace = _.isArray(log.stackTrace) ? log.stackTrace : [log.stackTrace];
      }
      return log;
    },
    async loadLog(id) {
      try {
        const log = await getSystemLog(id);
        this.log = this.formatLog(log);
      } catch(err) {
        this.onDataError(err);
      }
    },
    async subscribe(id) {
      this.serverEvents = new ServerEvents();
      this.serverEvents.$on("socket-message", (msg) => {
        // eslint-disable-next-line
        console.debug("log::socket-message", msg);
        if (msg.notificationType === "RESPONSE") {
          this.loadLog(this.id);
          if (msg.result === "RuntimeException: Process already finished") {
            this.serverEvents.disconnect();
          }
        } else if (msg.notificationType === "LOG_EVENT" && msg.eventType === "EVENT_LOG_CHANGED") {
          if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
          }
          this.updateTimeout = setTimeout(() => this.update(id), 500);
        } else if (msg.notificationType === "LOG_EVENT" && msg.eventType === "EVENT_LOG_FINISHED") {
          if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
          }
          this.updateTimeout = setTimeout(() => this.update(id), 500);
          this.serverEvents.disconnect();
        }
      });
      this.serverEvents.$on("socket-open", () => {
        this.serverEvents.sendMessage({ cmd: "subscribe-log-events", id: parseInt(id, 10) });
      });
      this.serverEvents.$on("socket-error", err => {
        this.onDataError(err);
        this.update(id);
      });
      this.serverEvents.$on("socket-close", () => {
        // eslint-disable-next-line
        // console.debug('socket-close', err);
        this.loadLog(id);
      });

      try {
        await this.serverEvents.connect();
      } catch(err) {
        //this.onDataError(err);
      }
    },
    async update(id) {
      try {
        const log = await getSystemLog(id);
        this.log = this.formatLog(log);
        setTimeout(() => {
          if (this.dataGrid) {
            this.dataGrid.refresh();
          }
        });
      } catch(err) {
        this.onDataError(err);
      }
    },
    onCancel() {
      this.$router.go(-1);
    },
    onRowClick(e) {
      if (window.getSelection().focusOffset !== window.getSelection().anchorOffset)
        return;

      const route = this.logRoute(e.data.id);

      if (e.event.metaKey || e.event.ctrlKey) {
        const routeData = this.$router.resolve(route);
        window.open(routeData.href, "_blank");
      } else {
        this.$router.push(route);
      }
    }
  },
  async created() {
    this.gridStateKey = 'gridLog';
    this.subscribe(this.id);
    this.loadLog(this.id);

    document.title = `${appInfo.title} - ${this.$t('logs.title')}`;
    this.$store.commit("setBreadcrumb", this.logBreadcrumb());
  },
  beforeDestroy() {
    if (!this.dataGrid)
      return;

    const state = this.dataGrid.state();
    this.stateSave(state);
  },
  destroyed() {
    if (this.serverEvents) {
      this.serverEvents.disconnect();
    }
  },
  watch: {
    id(val, old) {
      if (old > 0) {
        if (this.serverEvents) {
          this.serverEvents.disconnect();
        }
        this.log = null;
        document.title = `${appInfo.title} - ${this.$t('logs.title')}`;
        this.$store.commit("setBreadcrumb", this.logBreadcrumb());
        this.subscribe(this.id);
      }
    }
  },
};
</script>

<style lang="scss">
.app-log-message {
  margin-top: 1rem;
  p {
    margin: 0;
    color: #000;
  }
  pre {
    margin: 0;
    color: #000;
    white-space: pre-wrap;
    font-family: "Helvetica Neue", arial, sans-serif;
  }
}
.app-log-table {
  margin-top: 1rem;
}
.level-up {
  padding: 0 8px;
  color: #000 !important;
}
</style>
